import { ReactNode, useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  Home,
  Printer,
  FileText,
  Monitor,
  Settings,
  LogOut,
  Menu,
  X,
  Bell,
  User,
  Zap,
  Layout,
  Sparkles,
  Download,
  Calculator,
  HelpCircle,
  Mail,
  Building2,
  ChevronDown,
  ChevronRight,
  Calendar,
  MessageSquare,
  Layers,
  Flag,
  Shield,
  Palette,
  Target,
  Cloud,
  Star,
  Compass,
  PlayCircle,
  Grid3x3,
  PenTool,
  Users,
  BookOpen,
  FileEdit,
  ChevronLeft,
  ChevronsLeft
} from 'lucide-react'
import { useAuthStore } from '@/stores/authStore'
import { useTenantStore } from '@/stores/tenantStore'
import { useTranslation } from 'react-i18next'
import { loadLocale } from '@/i18n'
import { removeToken, apiClient } from '@/services/api'
import TenantSwitcher from '@/components/common/TenantSwitcher'

interface AppLayoutProps {
  children: ReactNode
}

interface NavItem {
  key: string
  href: string
  icon: any
  adminOnly?: boolean
  children?: NavItem[]
}

const navigation: NavItem[] = [
  { key: 'dashboard', href: '/dashboard', icon: Home },
  { key: 'quickPrint', href: '/jobs/quick', icon: Zap },
  { key: 'kiBon', href: '/ki-bon', icon: Sparkles },
  {
    key: 'challenges',
    href: '/challenges',
    icon: Calculator,
    children: [
      { key: 'challenges.standard', href: '/challenges', icon: Calculator },
      { key: 'challenges.story', href: '/story-challenges', icon: BookOpen },
      { key: 'challenges.library', href: '/story-library', icon: BookOpen }
    ]
  },
  {
    key: 'missions',
    href: '/missions',
    icon: Target,
    children: [
      { key: 'missions.browse', href: '/missions/browse', icon: Compass },
      { key: 'missions.running', href: '/missions/running', icon: PlayCircle },
      { key: 'missions.builder', href: '/missions/builder', icon: PenTool },
      { key: 'missions.onboarding', href: '/missions/onboarding', icon: Settings },
      { key: 'missions.taskTemplates', href: '/admin/task-templates', icon: FileEdit, adminOnly: true }
    ]
  },
  {
    key: 'widgets',
    href: '/widgets',
    icon: Layers,
    children: [
      { key: 'widgets.datetime', href: '/widgets/datetime', icon: Calendar },
      { key: 'widgets.quotes', href: '/widgets/quotes', icon: MessageSquare },
      { key: 'widgets.weather', href: '/widgets/weather', icon: Cloud },
      { key: 'widgets.astronomy', href: '/widgets/astronomy', icon: Star },
      { key: 'widgets.sudoku', href: '/widgets/sudoku', icon: Grid3x3 },
      { key: 'widgets.templates', href: '/widgets/templates', icon: FileText },
      { key: 'widgets.morningBriefing', href: '/morning-briefing', icon: Mail }
    ]
  },
  { key: 'printers', href: '/printers', icon: Printer },
  { key: 'jobs', href: '/jobs', icon: FileText },
  { key: 'templates', href: '/templates', icon: Layout },
  { key: 'devices', href: '/devices', icon: Monitor },
  { key: 'users', href: '/admin/users', icon: User, adminOnly: true },
  { key: 'tenants', href: '/admin/tenants', icon: Building2, adminOnly: true },
  { key: 'email', href: '/admin/email', icon: Mail, adminOnly: true },
  { key: 'i18n', href: '/admin/i18n', icon: Settings, adminOnly: true },
  { key: 'theme', href: '#theme', icon: Palette, adminOnly: true },
  { key: 'featureFlags', href: '/admin/feature-flags', icon: Flag, adminOnly: true },
  { key: 'gdprAdmin', href: '/admin/gdpr', icon: Shield, adminOnly: true },
  { key: 'otaUpdates', href: '/ota-updates', icon: Download, adminOnly: true },
  { key: 'privacy', href: '/privacy', icon: Shield },
  { key: 'settings', href: '/settings', icon: Settings },
  { key: 'help', href: '/help', icon: HelpCircle },
]

const AppLayout = ({ children }: AppLayoutProps) => {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [expandedMenus, setExpandedMenus] = useState<Record<string, boolean>>({})
  const { user, logout, isAdmin } = useAuthStore()
  const { currentTenant } = useTenantStore()
  const { t, i18n } = useTranslation('nav')
  const location = useLocation()

  const toggleMenu = (key: string) => {
    setExpandedMenus(prev => ({
      ...prev,
      [key]: !prev[key]
    }))
  }

  const handleLogout = async () => {
    try {
      const useCookie = ((import.meta.env.VITE_USE_COOKIE_TOKENS || 'false').toLowerCase() === 'true')
      if (useCookie) {
        await apiClient.post('/auth/logout')
      } else {
        removeToken()
      }
    } catch {}
    logout()
  }

  // Filter navigation based on user role
  const filteredNavigation = navigation.filter(item =>
    !item.adminOnly || isAdmin()
  )

  // Get actual href for navigation items (dynamic tenant ID for theme)
  const getItemHref = (item: NavItem) => {
    if (item.key === 'theme' && currentTenant) {
      return `/admin/tenants/${currentTenant.id}/theme`
    }
    return item.href
  }

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        >
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75" />
        </div>
      )}

      {/* Sidebar */}
      <div className={`
        fixed inset-y-0 left-0 z-50 bg-white shadow-lg transform lg:translate-x-0 lg:static lg:inset-0 transition-all duration-200 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        ${sidebarCollapsed ? 'lg:w-20' : 'lg:w-64'}
        w-64
      `}>
        <div className="flex flex-col h-full">
          {/* Logo and close button */}
          <div className="flex items-center justify-between p-4 border-b">
            <h1 className={`text-xl font-bold text-gray-900 transition-opacity duration-200 ${sidebarCollapsed ? 'lg:opacity-0 lg:hidden' : 'opacity-100'}`}>
              BonnyCloud
            </h1>
            {sidebarCollapsed && (
              <div className="hidden lg:block text-xl font-bold text-gray-900">B</div>
            )}
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-1 rounded-md hover:bg-gray-100"
            >
              <X className="w-5 h-5" />
            </button>
            {/* Desktop collapse toggle */}
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="hidden lg:block p-1 rounded-md hover:bg-gray-100"
              title={sidebarCollapsed ? 'Sidebar erweitern' : 'Sidebar einklappen'}
            >
              {sidebarCollapsed ? (
                <ChevronRight className="w-5 h-5" />
              ) : (
                <ChevronLeft className="w-5 h-5" />
              )}
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto p-4 space-y-2">
            {filteredNavigation.map((item) => {
              const itemHref = getItemHref(item)
              const isActive = location.pathname === itemHref
              const isParentActive = item.children?.some(child => location.pathname === child.href)
              const Icon = item.icon
              const isExpanded = expandedMenus[item.key] || isParentActive

              return (
                <div key={item.key}>
                  {/* Parent Item */}
                  {item.children ? (
                    <>
                      <button
                        onClick={() => toggleMenu(item.key)}
                        className={`
                          w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors
                          ${isParentActive
                            ? 'bg-primary-50 text-primary-700'
                            : 'text-gray-700 hover:bg-gray-100'
                          }
                          ${sidebarCollapsed ? 'lg:justify-center' : 'justify-between'}
                        `}
                        title={sidebarCollapsed ? t(item.key as any) : undefined}
                      >
                        <div className="flex items-center">
                          <Icon className={`${sidebarCollapsed ? 'w-7 h-7' : 'w-5 h-5 mr-3'}`} />
                          <span className={`transition-opacity duration-200 ${sidebarCollapsed ? 'lg:hidden' : ''}`}>
                            {t(item.key as any)}
                          </span>
                        </div>
                        <ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''} ${sidebarCollapsed ? 'lg:hidden' : ''}`} />
                      </button>

                      {/* Expandable Submenu */}
                      {isExpanded && (
                        <div className={`ml-6 mt-1 space-y-1 ${sidebarCollapsed ? 'lg:ml-0' : ''}`}>
                          {item.children
                            .filter(child => !child.adminOnly || isAdmin())
                            .map((child) => {
                              const isChildActive = location.pathname === child.href
                              const ChildIcon = child.icon

                              return (
                                <Link
                                  key={child.key}
                                  to={child.href}
                                  className={`
                                    flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors
                                    ${isChildActive
                                      ? 'bg-primary-100 text-primary-700'
                                      : 'text-gray-600 hover:bg-gray-50'
                                    }
                                    ${sidebarCollapsed ? 'lg:justify-center' : ''}
                                  `}
                                  onClick={() => setSidebarOpen(false)}
                                  title={sidebarCollapsed ? t(child.key as any) : undefined}
                                >
                                  <ChildIcon className={`${sidebarCollapsed ? 'w-5 h-5' : 'w-4 h-4 mr-3'}`} />
                                  <span className={`text-xs ${sidebarCollapsed ? 'lg:hidden' : ''}`}>
                                    {t(child.key as any)}
                                  </span>
                                </Link>
                              )
                            })}
                        </div>
                      )}
                    </>
                  ) : (
                    <Link
                      to={itemHref}
                      className={`
                        flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors
                        ${isActive
                          ? 'bg-primary-100 text-primary-700 border-r-2 border-primary-500'
                          : 'text-gray-700 hover:bg-gray-100'
                        }
                        ${sidebarCollapsed ? 'lg:justify-center' : ''}
                      `}
                      onClick={() => setSidebarOpen(false)}
                      title={sidebarCollapsed ? t(item.key as any) : undefined}
                    >
                      <Icon className={`${sidebarCollapsed ? 'w-7 h-7' : 'w-5 h-5 mr-3'}`} />
                      <span className={`transition-opacity duration-200 ${sidebarCollapsed ? 'lg:hidden' : ''}`}>
                        {t(item.key as any)}
                      </span>
                    </Link>
                  )}
                </div>
              )
            })}
          </nav>

          {/* User section */}
          <div className="p-4 border-t">
            {!sidebarCollapsed ? (
              <>
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                    <User className="w-4 h-4 text-primary-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {user?.username}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      {user?.role}
                    </p>
                  </div>
                </div>

                <button
                  onClick={handleLogout}
                  className="w-full flex items-center px-3 py-2 text-sm text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <LogOut className="w-4 h-4 mr-3" />
                  Sign out
                </button>
              </>
            ) : (
              <div className="hidden lg:flex flex-col items-center space-y-3">
                <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center" title={user?.username}>
                  <User className="w-4 h-4 text-primary-600" />
                </div>
                <button
                  onClick={handleLogout}
                  className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                  title="Sign out"
                >
                  <LogOut className="w-4 h-4 text-gray-700" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main content area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top bar */}
        <header className="bg-white border-b border-gray-200 px-4 py-3">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 rounded-md hover:bg-gray-100"
            >
              <Menu className="w-5 h-5" />
            </button>

            <div className="flex-1" />

            <div className="flex items-center space-x-4">
              {/* Tenant Switcher */}
              <TenantSwitcher />

              {/* Notifications */}
              <button className="p-2 rounded-md hover:bg-gray-100 relative">
                <Bell className="w-5 h-5 text-gray-600" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
              </button>

              {/* User menu */}
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                  <User className="w-4 h-4 text-primary-600" />
                </div>
                <span className="text-sm font-medium text-gray-900 hidden sm:block">
                  {user?.username}
                </span>
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 py-6">
            {children}
          </div>
        </main>
      </div>
    </div>
  )
}

export default AppLayout
